import './style.scss';

import cn from 'classnames';
import { UTCTimestamp } from 'lightweight-charts';
import isNil from 'lodash/isNil';
import React, { useCallback, useMemo, useState } from 'react';

import { useRelatedChartsEvent } from 'hooks/charts/useRelatedChartsEvent';
import { CHARTS_COLORS } from 'shared/consts/charts';
import { bn } from 'tools/math';
import { ICursorPosition, ILogicalRange, IPriceFormat } from 'types/charts';
import { chartFormatter } from 'utils/charts';
import { formatFiat } from 'utils/formats/formatFiat';

interface IBalanceTooltipProps {
  chartId: string;
  relatedCharts?: string[];
  priceFormat: IPriceFormat;
  balancesOuter: {
    records: {
      time: UTCTimestamp;
      value: number;
    }[];
    label: string;
  }[];
  cursorEvent: string;
  logicalEvent: string;
  totalBalancesUsd: {
    time: UTCTimestamp;
    value: number;
  }[];
}

const BalanceTooltip: React.FC<IBalanceTooltipProps> = ({
  chartId,
  relatedCharts,
  priceFormat,
  balancesOuter,
  cursorEvent,
  logicalEvent,
  totalBalancesUsd,
}) => {
  const onChangeLogicalRange = useCallback((p: any) => {
    _setVisibleLogicalRange(p);
  }, []);
  const [visibleLogicalRange, _setVisibleLogicalRange] = useState<ILogicalRange>(undefined);

  const [cursorPosition, _setCursorPosition] = useState<ICursorPosition>(undefined);

  const onChangeCursor = useCallback((p: ICursorPosition) => {
    _setCursorPosition(p);
  }, []);

  useRelatedChartsEvent({
    events: [`${chartId}-${cursorEvent}`].concat(
      (relatedCharts ?? []).map(ch => `${ch}-${cursorEvent}`),
    ),
    handler: (v: unknown) => onChangeCursor(v as unknown as any),
  });

  useRelatedChartsEvent({
    events: [`${chartId}-${logicalEvent}`].concat(
      (relatedCharts ?? []).map(ch => `${ch}-${logicalEvent}`),
    ),
    handler: (v: unknown) => onChangeLogicalRange(v as unknown as any),
  });

  const firstVisibleIndex = useMemo(
    () => (visibleLogicalRange ? Math.ceil(visibleLogicalRange.from) : undefined),
    [visibleLogicalRange],
  );
  const lastVisibleIndex = useMemo(
    () => (visibleLogicalRange ? Math.floor(visibleLogicalRange.to) : undefined),
    [visibleLogicalRange],
  );

  const hoveredIndex = useMemo(() => {
    const result = cursorPosition
      ? cursorPosition === undefined
        ? lastVisibleIndex
        : cursorPosition.logical
      : lastVisibleIndex;

    const recordsLength = balancesOuter[0]?.records.length;

    if (!result) return recordsLength - 1;

    return result > recordsLength - 1 ? recordsLength - 1 : result;
  }, [lastVisibleIndex, cursorPosition, balancesOuter]);

  const balances = useMemo(() => {
    return balancesOuter.map(el => ({
      value: el.records[hoveredIndex]?.value ?? 0,
      startValue:
        firstVisibleIndex !== undefined ? (el.records[firstVisibleIndex]?.value ?? 0) : undefined,
      label: el.label,
    }));
  }, [balancesOuter, firstVisibleIndex, hoveredIndex]);

  const totalBalance = useMemo(
    () => (priceFormat === 'usd' ? (totalBalancesUsd[hoveredIndex]?.value ?? 0) : undefined),
    [hoveredIndex, priceFormat, totalBalancesUsd],
  );

  const startTotalBalance = useMemo(
    () =>
      firstVisibleIndex !== undefined && priceFormat === 'usd'
        ? (totalBalancesUsd[firstVisibleIndex]?.value ?? 0)
        : undefined,
    [firstVisibleIndex, totalBalancesUsd, priceFormat],
  );

  const totalBalanceString = useMemo(() => {
    try {
      if (!totalBalance) return '';

      if (priceFormat === 'usd') {
        const bignumber = bn(totalBalance);

        return formatFiat(bignumber, 18);
      }

      return chartFormatter(totalBalance);
    } catch (e) {
      return '0';
    }
  }, [totalBalance, priceFormat]);

  const totalBalanceDiff = useMemo(
    () =>
      !isNil(totalBalance) && !isNil(startTotalBalance)
        ? totalBalance - startTotalBalance
        : undefined,
    [startTotalBalance, totalBalance],
  );

  const totalBalanceDiffString = useMemo(() => {
    try {
      if (!totalBalanceDiff) return '';

      if (priceFormat === 'usd') {
        const bignumber = bn(totalBalanceDiff);

        return formatFiat(bignumber, 18);
      }

      return chartFormatter(totalBalanceDiff);
    } catch (e) {
      return '';
    }
  }, [totalBalanceDiff, priceFormat]);

  const diffPercentage = useMemo(
    () =>
      !isNil(startTotalBalance) && startTotalBalance !== 0 && !isNil(totalBalanceDiff)
        ? Number(Math.abs((totalBalanceDiff / startTotalBalance) * 100).toFixed(2))
        : undefined,
    [totalBalanceDiff, startTotalBalance],
  );

  const _balances = useMemo(
    () =>
      balances.map(balance => {
        let balanceString = chartFormatter(balance.value);

        if (priceFormat === 'usd') {
          balanceString = formatFiat(bn(balance.value), 18);
        }

        const diff = !isNil(balance.startValue) ? balance.value - balance.startValue : undefined;

        const diffPercentage =
          !isNil(balance.startValue) && balance.startValue !== 0 && !isNil(diff)
            ? Number(Math.abs((diff / balance.startValue) * 100).toFixed(2))
            : undefined;

        let diffString = !isNil(diff) ? chartFormatter(diff) : '';
        if (priceFormat === 'usd' && !isNil(diff)) {
          diffString = formatFiat(bn(diff), 18);
        }

        return {
          label: balance.label,
          balanceString,
          diff,
          diffPercentage,
          diffString,
        };
      }),
    [balances, priceFormat],
  );

  return (
    <div className="mm-pair-balance-chart-tooltip">
      {!isNil(totalBalance) && (
        <div className="tooltip-balance">
          <div className="total-balance-value">
            <span className="caption">Total:</span>
            <span className="amount _val" style={{ color: CHARTS_COLORS[3] }}>
              {totalBalanceString}
            </span>
            {totalBalanceDiff !== undefined && diffPercentage !== undefined && (
              <div className="value">
                <span
                  className={cn('amount', {
                    green: totalBalanceDiff >= 0,
                    red: totalBalanceDiff < 0,
                  })}
                >
                  {totalBalanceDiff > 0 ? '+' : ''}
                  {totalBalanceDiffString}
                </span>
                <span
                  className={cn('amount', {
                    green: totalBalanceDiff >= 0,
                    red: totalBalanceDiff < 0,
                  })}
                >
                  ({totalBalanceDiff >= 0 ? '+' : '-'}
                  {diffPercentage}%)
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {_balances.map((el, idx) => (
        <div className="tooltip-balance" key={idx}>
          <div className="total-balance-value">
            <span className="caption">{el.label}</span>
            <span className="amount _val" style={{ color: CHARTS_COLORS[idx] }}>
              {el.balanceString}
            </span>
            {el.diff !== undefined && el.diffPercentage !== undefined && (
              <div className="value">
                <span
                  className={cn('amount', {
                    green: el.diff >= 0,
                    red: el.diff < 0,
                  })}
                >
                  {el.diff > 0 ? '+' : ''}
                  {el.diffString}
                </span>
                <span
                  className={cn('amount', {
                    green: el.diff >= 0,
                    red: el.diff < 0,
                  })}
                >
                  ({el.diff >= 0 ? '+' : '-'}
                  {el.diffPercentage}%)
                </span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export { BalanceTooltip };
